import { useState, useRef, useEffect, type ChangeEvent, type FormEvent } from 'react';
import { makeConsultation } from '@/api/partnerShopDetail';
import type { ConsultationShopData } from '@/pages/detail/types';

type Mode = 'form' | 'submitting' | 'success' | 'error';

interface ConsultationModalProps {
  shop: ConsultationShopData;
  onClose: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  dress: '예복',
  jewelry: '예물',
  hanbok: '한복',
};

export default function ConsultationModal({ shop, onClose }: ConsultationModalProps) {
  const [mode, setMode] = useState<Mode>('form');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState('');

  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const dateRef = useRef<HTMLInputElement>(null);
  const requestRef = useRef<HTMLTextAreaElement>(null);
  const consentRef = useRef<HTMLInputElement>(null);
  const honeypotRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handleEsc = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && mode !== 'submitting') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [mode, onClose]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    const name = nameRef.current?.value.trim() || '';
    const phone = phoneRef.current?.value.trim() || '';
    const date = dateRef.current?.value || '';

    if (!name) newErrors.name = '이름을 입력해주세요.';
    if (!phone) {
      newErrors.phone = '전화번호를 입력해주세요.';
    } else if (phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = '올바른 전화번호를 입력해주세요.';
    }
    if (!date) newErrors.date = '방문희망일을 선택해주세요.';
    if (!consentRef.current?.checked) newErrors.consent = '개인정보 수집 및 이용에 동의해주세요.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearError = (field: string) => {
    setErrors(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const honeypot = honeypotRef.current?.value?.trim() || '';
    if (honeypot) {
      setMode('success');
      return;
    }

    setMode('submitting');

    try {
      const result = await makeConsultation({
        shopSlug: shop.shopSlug,
        name: nameRef.current?.value.trim() || '',
        phone: phoneRef.current?.value.trim() || '',
        visitDate: dateRef.current?.value || '',
        etc: requestRef.current?.value.trim() || '',
      });

      if (result?.status && result.status !== 200) {
        setServerError(result.message || '상담 문의 접수 중 문제가 발생했어요.\n잠시 후 다시 시도해주세요.');
        setMode('error');
        return;
      }

      setMode('success');
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      setServerError(message || '상담 문의 접수 중 문제가 발생했어요.\n잠시 후 다시 시도해주세요.');
      setMode('error');
    }
  };

  const handleRetry = () => {
    setMode('form');
    setServerError('');
  };

  const handleBackdropClick = () => {
    if (mode !== 'submitting') onClose();
  };

  const handlePhoneInput = (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length > 3 && value.length <= 7) {
      value = value.slice(0, 3) + '-' + value.slice(3);
    } else if (value.length > 7) {
      value = value.slice(0, 3) + '-' + value.slice(3, 7) + '-' + value.slice(7, 11);
    }
    e.target.value = value;
    clearError('phone');
  };

  return (
    <div
      className="fixed inset-0 flex items-end md:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="상담 문의하기"
      style={{zIndex: 1000}}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}
        onClick={handleBackdropClick}
      />

      {/* Modal Card — desktop: centered, mobile: bottom sheet */}
      <div
        className="relative w-full md:max-w-[460px] md:rounded-3xl bg-white shadow-2xl overflow-hidden flex flex-col rounded-t-3xl rounded-b-none"
        style={{ maxHeight: '92vh' }}
      >
        {/* Bottom sheet handle (mobile only) */}
        {/* <div className="md:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-[#D1CCC5]" />
        </div> */}
        {/* Header */}
        {mode === 'form' && (
          <div className="px-5 pt-4 md:pt-6 pb-2">
            <div className='flex item-center justify-between'  style={{alignItems: 'center'}}>
              <div className="flex item-center text-[19px] md:text-[20px] font-bold text-[#211C18] leading-tight">
                상담 문의하기
              </div>
              <button
                onClick={onClose}
                disabled={mode === 'submitting'}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-[#F5F3F0] transition-colors flex-shrink-0 ml-3 cursor-pointer disabled:opacity-30"
                aria-label="닫기"
              >
                <i className="ri-close-line text-xl text-[#5F5953]"></i>
              </button>  
            </div>
            <p className="text-[14px] text-[#5F5953] mt-1 leading-relaxed">
              방문 희망일과 연락처를 남겨주시면<br className="md:hidden" />담당자가 확인 후 연락드릴게요.
            </p>
            
          </div>
        )}

        {/* Scrollable content */}
        <div className="overflow-y-auto px-5 pb-6" ref={bottomRef}>
          {mode === 'form' && (
            <>
              {/* Vendor Info Summary */}
              <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-[#FAF7F2] border border-[#E1DBD4] mb-5">
                <div className="w-10 h-10 rounded-full bg-[#B4563F] flex items-center justify-center flex-shrink-0">
                  <i className="ri-store-2-line text-white text-lg"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold text-[#211C18] truncate">{shop.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[12px] text-[#7A736C]">{shop.location} · {CATEGORY_LABELS[shop.category] || shop.category}</span>
                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#F8F1EA] text-[#8A5A2B] leading-none">인증업체</span>
                  </div>
                </div>
              </div>

              <form id="consultation-form" onSubmit={handleSubmit} noValidate>
                {/* Honeypot */}
                <input
                  ref={honeypotRef}
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute opacity-0 pointer-events-none"
                  style={{ position: 'absolute', left: '-9999px' }}
                />

                {/* Hidden vendor info for form submission */}
                <input type="hidden" name="vendor_name" value={shop.name} />
                <input type="hidden" name="vendor_location" value={shop.location} />
                <input type="hidden" name="vendor_category" value={CATEGORY_LABELS[shop.category] || shop.category} />

                <div className="flex flex-col gap-4">
                  {/* Name */}
                  <div>
                    <label className="block text-[13px] font-medium text-[#3F3A35] mb-1.5">
                      이름 <span className="text-[#B4563F]">*</span>
                    </label>
                    <input
                      ref={nameRef}
                      type="text"
                      name="name"
                      placeholder="이름을 입력해주세요"
                      onChange={() => clearError('name')}
                      className={`w-full h-[46px] px-4 rounded-xl border bg-white text-[15px] text-[#211C18] placeholder-[#B5AFA8] outline-none transition-colors ${
                        errors.name ? 'border-[#D9705A]' : 'border-[#E1DBD4] focus:border-[#B4563F]'
                      }`}
                    />
                    {errors.name && (
                      <p className="text-[12px] text-[#D9705A] mt-1.5 ml-1">{errors.name}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-[13px] font-medium text-[#3F3A35] mb-1.5">
                      전화번호 <span className="text-[#B4563F]">*</span>
                    </label>
                    <input
                      ref={phoneRef}
                      type="tel"
                      name="phone"
                      placeholder="010-0000-0000"
                      maxLength={13}
                      onChange={handlePhoneInput}
                      className={`w-full h-[46px] px-4 rounded-xl border bg-white text-[15px] text-[#211C18] placeholder-[#B5AFA8] outline-none transition-colors ${
                        errors.phone ? 'border-[#D9705A]' : 'border-[#E1DBD4] focus:border-[#B4563F]'
                      }`}
                    />
                    <p className="text-[12px] text-[#7A736C] mt-1 ml-1">연락 가능한 번호를 입력해주세요.</p>
                    {errors.phone && (
                      <p className="text-[12px] text-[#D9705A] mt-1 ml-1">{errors.phone}</p>
                    )}
                  </div>

                  {/* Visit Date */}
                  <div>
                    <label className="block text-[13px] font-medium text-[#3F3A35] mb-1.5">
                      방문희망일 <span className="text-[#B4563F]">*</span>
                    </label>
                    <div className="relative">
                      <input
                        ref={dateRef}
                        type="date"
                        name="visit_date"
                        onChange={() => clearError('date')}
                        min={new Date().toISOString().split('T')[0]}
                        className={`w-full h-[46px] px-4 rounded-xl border bg-white text-[15px] text-[#211C18] placeholder-[#B5AFA8] outline-none transition-colors appearance-none ${
                          errors.date ? 'border-[#D9705A]' : 'border-[#E1DBD4] focus:border-[#B4563F]'
                        }`}
                        style={{ colorScheme: 'light' }}
                      />
                      <i className="ri-calendar-line absolute right-4 top-1/2 -translate-y-1/2 text-[#7A736C] text-lg pointer-events-none"></i>
                    </div>
                    {errors.date && (
                      <p className="text-[12px] text-[#D9705A] mt-1.5 ml-1">{errors.date}</p>
                    )}
                  </div>

                  {/* Request */}
                  <div>
                    <label className="block text-[13px] font-medium text-[#3F3A35] mb-1.5">
                      요청사항
                    </label>
                    <textarea
                      ref={requestRef}
                      name="request"
                      placeholder="원하는 상담 내용이나 궁금한 점을 적어주세요"
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-[#E1DBD4] bg-white text-[15px] text-[#211C18] placeholder-[#B5AFA8] outline-none focus:border-[#B4563F] transition-colors resize-none"
                    />
                  </div>

                  {/* Privacy Consent */}
                  <div className="pt-1">
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        ref={consentRef}
                        type="checkbox"
                        name="privacy_consent"
                        onChange={() => clearError('consent')}
                        className="mt-0.5 w-4 h-4 rounded border-[#D1CCC5] text-[#B4563F] focus:ring-[#B4563F] cursor-pointer accent-[#B4563F] flex-shrink-0"
                      />
                      <span className="text-[12px] text-[#7A736C] leading-relaxed">
                        상담 문의를 위해 입력하신 정보가 업체 상담 연결 목적으로 사용됩니다.
                      </span>
                    </label>
                    {errors.consent && (
                      <p className="text-[12px] text-[#D9705A] mt-1.5 ml-1">{errors.consent}</p>
                    )}
                  </div>
                </div>

                {/* Desktop submit button (inside scroll) */}
                <div className="hidden md:block mt-6">
                  <button
                    type="submit"
                    className="w-full h-[48px] rounded-full bg-[#B4563F] text-white text-[15px] font-medium hover:bg-[#9F4633] active:bg-[#8C3E2D] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    상담 문의하기
                  </button>
                </div>
              </form>
            </>
          )}

          {mode === 'submitting' && (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="w-10 h-10 rounded-full border-2 border-[#E1DBD4] border-t-[#B4563F] animate-spin mb-4" />
              <p className="text-[15px] text-[#3F3A35] font-medium">접수 중...</p>
            </div>
          )}

          {mode === 'success' && (
            <div className="flex flex-col items-center text-center py-8">
              <div className="w-14 h-14 rounded-full bg-[#F5F3F0] flex items-center justify-center mb-5">
                <i className="ri-check-line text-[#B4563F] text-[28px]"></i>
              </div>
              <h3 className="text-[19px] font-bold text-[#211C18] mb-2">상담 문의가 접수되었어요</h3>
              <p className="text-[14px] text-[#5F5953] leading-relaxed max-w-[300px]">
                담당자가 확인 후 연락드릴 예정입니다.<br />
                방문 일정은 업체 상황에 따라 조율될 수 있어요.
              </p>
              <button
                onClick={onClose}
                className="mt-6 w-full h-[48px] rounded-full bg-[#B4563F] text-white text-[15px] font-medium hover:bg-[#9F4633] transition-colors cursor-pointer whitespace-nowrap"
              >
                확인
              </button>
            </div>
          )}

          {mode === 'error' && (
            <div className="flex flex-col items-center text-center py-8">
              <div className="w-14 h-14 rounded-full bg-[#FFF5F3] flex items-center justify-center mb-5">
                <i className="ri-error-warning-line text-[#D9705A] text-[28px]"></i>
              </div>
              <h3 className="text-[17px] font-semibold text-[#211C18] mb-2">접수 중 문제가 발생했어요</h3>
              <p className="text-[14px] text-[#5F5953] leading-relaxed max-w-[300px] whitespace-pre-line">
                {serverError || '잠시 후 다시 시도해주세요.'}
              </p>
              <button
                onClick={handleRetry}
                className="mt-6 w-full h-[48px] rounded-full bg-[#B4563F] text-white text-[15px] font-medium hover:bg-[#9F4633] transition-colors cursor-pointer whitespace-nowrap"
              >
                다시 시도
              </button>
            </div>
          )}
        </div>

        {/* Mobile sticky submit button (only in form mode) */}
        {mode === 'form' && (
          <div
            className="md:hidden px-5 py-3 border-t border-[#E1DBD4] bg-white flex-shrink-0"
            style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
          >
            <button
              type="submit"
              form="consultation-form"
              className="w-full h-[50px] rounded-full bg-[#B4563F] text-white text-[15px] font-medium hover:bg-[#9F4633] active:bg-[#8C3E2D] transition-colors cursor-pointer whitespace-nowrap"
            >
              상담 문의하기
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
